import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  SimpleGrid,
  Text,
  Button,
  Badge,
  Icon,
  Progress,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { FaCoins, FaInfoCircle, FaBolt } from "react-icons/fa";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import { fmtNombre } from "variables/veille";
import { useVeilles } from "contexts/VeillesContext";
import { useSession } from "contexts/SessionContext";
import { supabase } from "lib/supabase";

// Montant en centimes -> "1 200 €"
const fmtMontant = (cents, devise) =>
  `${Math.round((cents || 0) / 100).toLocaleString("fr-FR")} ${devise === "EUR" ? "€" : devise || ""}`.trim();
const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }) : "—";

export default function Utilisation() {
  const textColor = useColorModeValue("gray.700", "white");
  const toast = useToast();
  const { getVeille } = useVeilles();
  const { org } = useSession();

  // Données RÉELLES : portefeuille de tokens, abonnement, factures, consommation par veille.
  const [wallet, setWallet] = useState(null);
  const [sub, setSub] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [parVeille, setParVeille] = useState([]);
  const [chargement, setChargement] = useState(true);

  useEffect(() => {
    let actif = true;
    (async () => {
      const [w, s, inv, tx] = await Promise.all([
        supabase.from("token_wallets").select("inclus_par_mois, solde, renouvellement_at").maybeSingle(),
        supabase.from("subscriptions").select("statut, current_period_end, plans(label)").maybeSingle(),
        supabase.from("invoices").select("id, libelle, emise_le, montant_cents, devise, statut").order("emise_le", { ascending: false }),
        supabase.from("token_transactions").select("veille_id, montant").eq("type", "consommation"),
      ]);
      if (!actif) return;
      setWallet(w.data || null);
      setSub(s.data || null);
      setInvoices(inv.data || []);
      // Agrège la consommation réelle par veille (on ignore les lignes sans veille rattachée).
      const agg = {};
      (tx.data || []).forEach((t) => {
        if (t.veille_id) agg[t.veille_id] = (agg[t.veille_id] || 0) + Math.abs(t.montant || 0);
      });
      setParVeille(
        Object.entries(agg)
          .map(([veille_id, tokens]) => ({ veille_id, tokens }))
          .sort((a, b) => b.tokens - a.tokens)
      );
      setChargement(false);
    })();
    return () => { actif = false; };
  }, [org && org.id]);

  const inclus = (wallet && wallet.inclus_par_mois) || 0;
  const restants = (wallet && wallet.solde) || 0;
  const consommes = Math.max(0, inclus - restants);
  const pct = inclus > 0 ? Math.round((consommes / inclus) * 100) : 0;
  const bientot = pct >= 80;
  const planLabel = (sub && sub.plans && sub.plans.label) || "—";
  const maxVeille = Math.max(...parVeille.map((p) => p.tokens), 1);

  // Le paiement en ligne n'est pas encore branché : on l'annonce clairement plutôt que de le simuler.
  function recharger() {
    toast({
      title: "Recharge de tokens",
      description: "Le paiement en ligne arrive bientôt. Pour ajouter des tokens dès maintenant, contactez-nous à support@wave-veille.fr.",
      status: "info",
      duration: 5000,
      isClosable: true,
    });
  }

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
      {/* En-tête */}
      <Flex justify="space-between" align={{ base: "flex-start", md: "center" }} direction={{ base: "column", md: "row" }} mb="28px" gap="16px">
        <Flex align="center">
          <Box w="7px" h="60px" borderRadius="full" bg="teal.300" me="18px" flexShrink={0} />
          <Box>
            <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight="bold" color={textColor} lineHeight="1.1">Utilisation</Text>
            <Text fontSize="md" color="gray.700" mt="4px">Suivez votre consommation de tokens et vos factures.</Text>
          </Box>
        </Flex>
        <Button bg="teal.300" color="white" _hover={{ bg: "teal.400" }} borderRadius="12px" size="lg" leftIcon={<Icon as={FaCoins} />} onClick={recharger}>Recharger des tokens</Button>
      </Flex>

      {/* Solde de tokens — carte principale (données réelles) */}
      <Card mb="24px">
        <CardBody p={{ base: "22px", md: "30px" }}>
          <Flex direction="column" w="100%">
            <Flex justify="space-between" align={{ base: "flex-start", md: "flex-end" }} direction={{ base: "column", md: "row" }} gap="16px" mb="20px">
              <Box>
                <Flex align="center" gap="10px" mb="6px">
                  <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="0.5px">Tokens restants ce mois</Text>
                  <Badge bg="teal.50" color="teal.600" borderRadius="6px" textTransform="none" fontWeight="bold">Plan {planLabel}</Badge>
                </Flex>
                <Text fontSize={{ base: "3xl", md: "4xl" }} fontWeight="bold" color="gray.800" lineHeight="1">{fmtNombre(restants)}</Text>
                <Text fontSize="sm" color="gray.600" mt="6px">sur {fmtNombre(inclus)} inclus · renouvellement le {fmtDate(wallet && wallet.renouvellement_at)}</Text>
                <Text fontSize="xs" color="gray.500" mt="4px">1 token = 1 retombée collectée et analysée par le moteur.</Text>
              </Box>
              {bientot && (
                <Flex align="center" gap="8px" bg="orange.50" color="orange.600" px="12px" py="8px" borderRadius="10px">
                  <Icon as={FaBolt} fontSize="12px" />
                  <Text fontSize="sm" fontWeight="bold">Bientôt épuisé — pensez à recharger</Text>
                </Flex>
              )}
            </Flex>
            <Progress value={pct} borderRadius="full" size="md" sx={{ "& > div": { background: `var(--chakra-colors-${bientot ? "orange-400" : "teal-400"})` } }} bg="gray.100" />
            <Flex justify="space-between" mt="8px">
              <Text fontSize="xs" color="gray.500">{fmtNombre(consommes)} consommés ({pct} %)</Text>
              <Text fontSize="xs" color="gray.500">{fmtNombre(inclus)}</Text>
            </Flex>
          </Flex>
        </CardBody>
      </Card>

      {/* Indicateurs (données réelles) */}
      <Box bg="gray.100" borderRadius="20px" p={{ base: "16px", md: "20px" }} mb="24px">
        <SimpleGrid columns={{ base: 2, xl: 4 }} spacing="20px">
          <StatCard label="Plan actuel" value={planLabel} />
          <StatCard label="Tokens inclus / mois" value={fmtNombre(inclus)} />
          <StatCard label="Consommés" value={fmtNombre(consommes)} />
          <StatCard label="Restants" value={fmtNombre(restants)} />
        </SimpleGrid>
      </Box>

      {/* Consommation par veille + facturation */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing="24px">
        <Card>
          <CardBody p={{ base: "20px", md: "24px" }}>
            <Flex direction="column" w="100%">
              <Box mb="18px">
                <Text fontSize="lg" fontWeight="bold" color={textColor}>Consommation par veille</Text>
                <Text fontSize="sm" color="gray.600">Tokens consommés par le moteur, par veille.</Text>
              </Box>
              {parVeille.length === 0 ? (
                <Text fontSize="sm" color="gray.500" py="20px">{chargement ? "Chargement…" : "Aucune consommation pour l'instant."}</Text>
              ) : (
                <Flex direction="column" gap="16px">
                  {parVeille.map((p) => {
                    const v = getVeille(p.veille_id);
                    return (
                      <Box key={p.veille_id}>
                        <Flex justify="space-between" mb="6px">
                          <Text fontSize="sm" fontWeight="bold" color="gray.800" noOfLines={1}>{v ? (v.client || v.nom) : "Veille"}</Text>
                          <Text fontSize="sm" color="gray.600">{fmtNombre(p.tokens)}</Text>
                        </Flex>
                        <Progress value={(p.tokens / maxVeille) * 100} borderRadius="full" size="sm" sx={{ "& > div": { background: "var(--chakra-colors-teal-400)" } }} bg="gray.100" />
                      </Box>
                    );
                  })}
                </Flex>
              )}
            </Flex>
          </CardBody>
        </Card>

        <Card>
          <CardBody p={{ base: "20px", md: "24px" }}>
            <Flex direction="column" w="100%">
              {/* Paiement — annoncé honnêtement tant qu'aucun prestataire n'est branché */}
              <Box mb="20px">
                <Text fontSize="lg" fontWeight="bold" color={textColor} mb="12px">Moyen de paiement</Text>
                <Flex align="center" gap="12px" p="16px" borderRadius="14px" border="1px dashed" borderColor="gray.200" bg="gray.50">
                  <Icon as={FaInfoCircle} color="teal.400" fontSize="18px" flexShrink={0} />
                  <Box>
                    <Text fontSize="sm" fontWeight="bold" color="gray.800">Paiement en ligne bientôt disponible</Text>
                    <Text fontSize="xs" color="gray.600">La gestion de l'abonnement et des recharges se fait pour l'instant avec notre équipe.</Text>
                  </Box>
                </Flex>
              </Box>

              {/* Historique de facturation (factures réelles) */}
              <Text fontSize="lg" fontWeight="bold" color={textColor} mb="12px">Historique de facturation</Text>
              {invoices.length === 0 ? (
                <Text fontSize="sm" color="gray.500" py="10px">{chargement ? "Chargement…" : "Aucune facture pour l'instant."}</Text>
              ) : (
                <Flex direction="column">
                  {invoices.map((f, i) => (
                    <Flex key={f.id} align="center" py="12px" borderTop={i === 0 ? "none" : "1px solid"} borderColor="gray.100" gap="12px">
                      <Box flex="1" minW="0">
                        <Text fontSize="sm" fontWeight="bold" color="gray.800" noOfLines={1}>{f.libelle}</Text>
                        <Text fontSize="xs" color="gray.500">{fmtDate(f.emise_le)}</Text>
                      </Box>
                      <Text fontSize="sm" fontWeight="bold" color="gray.800" whiteSpace="nowrap">{fmtMontant(f.montant_cents, f.devise)}</Text>
                      <Badge colorScheme={f.statut === "payee" ? "green" : "orange"} borderRadius="6px">{f.statut === "payee" ? "Payée" : f.statut}</Badge>
                    </Flex>
                  ))}
                </Flex>
              )}
            </Flex>
          </CardBody>
        </Card>
      </SimpleGrid>
    </Flex>
  );
}

function StatCard({ label, value }) {
  return (
    <Card>
      <CardBody p="22px">
        <Flex direction="column" w="100%">
          <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="0.5px">{label}</Text>
          <Text fontSize="3xl" fontWeight="bold" color="gray.800" mt="8px" lineHeight="1">{value}</Text>
        </Flex>
      </CardBody>
    </Card>
  );
}
